import { Check, Loader2, X } from 'lucide-react'
import { createContext, ReactNode, useCallback, useContext, useState } from 'react'
import ROUTES from '@/ROUTES'
import { usePaywall } from '@/stripe/PayWallProvider'

import { api } from '@/services/api.service'

import Loading from '@/components/Loading'

interface IPaywallModalContext {
    showSubscriptionModal: () => void
    hideSubscriptionModal: () => void
    isModalOpen: boolean
}

const PaywallModalContext = createContext<IPaywallModalContext | undefined>(undefined)

export const usePaywallModal = () => {
    const context = useContext(PaywallModalContext)
    if (!context) {
        throw new Error('usePaywallModal must be used within PaywallGate')
    }
    return context
}

interface PaywallGateProps {
    children: ReactNode
    fallback?: ReactNode
    returnUrl?: string
    blockAccess?: boolean
}

export default function PaywallGate({ children, fallback, returnUrl, blockAccess = false }: PaywallGateProps) {
    const { hasAccess, isLoading } = usePaywall()
    const [isModalOpen, setIsModalOpen] = useState(false)

    const showSubscriptionModal = useCallback(() => {
        setIsModalOpen(true)
    }, [])

    const hideSubscriptionModal = useCallback(() => {
        setIsModalOpen(false)
    }, [])

    if (isLoading) {
        return <Loading />
    }

    if (blockAccess && !hasAccess) {
        if (fallback) {
            return <>{fallback}</>
        }
        return <SubscriptionModal isOpen={true} onClose={() => {}} returnUrl={returnUrl} />
    }

    return (
        <PaywallModalContext.Provider
            value={{
                showSubscriptionModal,
                hideSubscriptionModal,
                isModalOpen,
            }}
        >
            {children}
            <SubscriptionModal isOpen={isModalOpen} onClose={hideSubscriptionModal} returnUrl={returnUrl} />
        </PaywallModalContext.Provider>
    )
}

interface ISubscriptionModal {
    isOpen: boolean
    onClose: () => void
    returnUrl?: string
}

function SubscriptionModal({ isOpen, onClose, returnUrl }: ISubscriptionModal) {
    const { isTestMode, trialPeriodDays, paywallMode } = usePaywall()
    const [plan, setPlan] = useState<'monthly' | 'annual'>('monthly')
    const [isLoading, setIsLoading] = useState(false)
    const [checkoutError, setCheckoutError] = useState<string | null>(null)

    if (!isOpen) return null

    const redirectToPaymentGateway = () => {
        setIsLoading(true)
        setCheckoutError(null)
        api<{ url: string }>(ROUTES.stripe.createCheckoutSession + '/' + plan)
            .then((res) => {
                if (res.status >= 400 || !res.data?.url) {
                    setCheckoutError("We're experiencing a temporary issue processing your request. Our team has been notified and is working to resolve it. Thank you for your patience!")
                    return
                }
                window.location.replace(res.data.url)
            })
            .catch(() => {
                setCheckoutError("We're experiencing a temporary issue processing your request. Our team has been notified and is working to resolve it. Thank you for your patience!")
            })
            .finally(() => setIsLoading(false))
    }

    if (paywallMode === 'usage') {
        return <UsagePaywallModal
            isOpen={isOpen}
            onClose={onClose}
            isTestMode={isTestMode}
            plan={plan}
            setPlan={setPlan}
            isLoading={isLoading}
            checkoutError={checkoutError}
            redirectToPaymentGateway={redirectToPaymentGateway}
        />
    }

    return (
        <>
            <div
                className='fixed inset-0 z-40 min-h-screen transition-opacity bg-black/50 backdrop-blur-sm'
                onClick={onClose}
            />

            <div className='fixed inset-0 z-50 overflow-y-auto'>
                <div className='flex items-center justify-center min-h-full p-4'>
                    <div className='relative w-full max-w-2xl overflow-hidden transition-all transform shadow-2xl rounded-3xl bg-gradient-to-br from-cyan-600 via-cyan-500 to-cyan-600'>
                        <button
                            onClick={onClose}
                            className='absolute z-10 p-2 text-white transition-colors rounded-full right-4 top-4 bg-white/20 hover:bg-white/30'
                        >
                            <X className='w-5 h-5' />
                        </button>

                        <div className='p-8 md:p-10'>
                            {isTestMode && (
                                <div className='mb-4 px-4 py-2 bg-orange-400 rounded-lg text-center'>
                                    <span className='text-sm font-bold text-white uppercase tracking-wide'>
                                        Stripe Test Mode — No real charges
                                    </span>
                                </div>
                            )}
                            <div className='mb-8 text-center'>
                                <div className='flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-white/20'>
                                    <svg
                                        className='w-8 h-8 text-white'
                                        fill='none'
                                        viewBox='0 0 24 24'
                                        stroke='currentColor'
                                    >
                                        <path
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            strokeWidth={2}
                                            d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'
                                        />
                                    </svg>
                                </div>
                                <h2 className='mb-3 text-3xl font-bold text-white drop-shadow-lg'>
                                    Unlock Full Access
                                </h2>
                                <p className='text-lg text-white/90 drop-shadow-md'>
                                    Start your personalized autism care journey with all premium features
                                </p>
                            </div>

                            <div className='grid gap-4 mb-6 sm:grid-cols-2'>
                                <button
                                    type='button'
                                    onClick={() => setPlan('monthly')}
                                    className={`relative rounded-2xl border p-6 text-left transition-all duration-200 ${
                                        plan === 'monthly'
                                            ? 'border-white bg-white text-cyan-600 shadow-lg'
                                            : 'border-white/40 bg-white/15 text-white hover:bg-white/25'
                                    }`}
                                >
                                    <div className='space-y-3'>
                                        <div className='flex items-center justify-between'>
                                            <h3 className='text-xl font-bold'>Monthly</h3>
                                            {plan === 'monthly' && (
                                                <div className='flex items-center justify-center w-5 h-5 rounded-full bg-cyan-600'>
                                                    <Check className='w-3 h-3 text-white' />
                                                </div>
                                            )}
                                        </div>
                                        <div className='space-y-1'>
                                            <div className='text-4xl font-extrabold'>$4.99</div>
                                            <div className='text-lg line-through opacity-75'>$9.99</div>
                                            <div className='text-sm opacity-75'>per month</div>
                                        </div>
                                        {trialPeriodDays > 0 && (
                                            <div className='px-3 py-2 border rounded-lg border-yellow-100/30 bg-gradient-to-r from-yellow-100/20 to-orange-400/20'>
                                                <p className='text-sm font-medium'>✨ {trialPeriodDays}-day free trial included (no card required)</p>
                                            </div>
                                        )}
                                    </div>
                                </button>

                                <button
                                    type='button'
                                    onClick={() => setPlan('annual')}
                                    className={`relative rounded-2xl border p-6 text-left transition-all duration-200 ${
                                        plan === 'annual'
                                            ? 'border-white bg-white text-cyan-600 shadow-lg'
                                            : 'border-white/40 bg-white/15 text-white hover:bg-white/25'
                                    }`}
                                >
                                    <div className='absolute px-3 py-1 text-xs font-bold bg-yellow-400 rounded-full -right-1 -top-1 text-cyan-700'>
                                        BEST VALUE
                                    </div>
                                    <div className='space-y-3'>
                                        <div className='flex items-center justify-between'>
                                            <h3 className='text-xl font-bold'>Annual</h3>
                                            {plan === 'annual' && (
                                                <div className='flex items-center justify-center w-5 h-5 rounded-full bg-cyan-600'>
                                                    <Check className='w-3 h-3 text-white' />
                                                </div>
                                            )}
                                        </div>
                                        <div className='space-y-1'>
                                            <div className='text-4xl font-extrabold'>$49</div>
                                            <div className='text-lg line-through opacity-75'>$99</div>
                                            <div className='text-sm opacity-75'>per year • Save $10.88</div>
                                        </div>
                                        <div className='px-3 py-2 border rounded-lg border-yellow-400/30 bg-gradient-to-r from-yellow-400/20 to-orange-400/20'>
                                            <p className='text-sm font-medium'>✨ 30-day free trial included (no card required)</p>
                                        </div>
                                    </div>
                                </button>
                            </div>

                            <div className='p-6 mb-6 border rounded-2xl border-white/30 bg-white/10 backdrop-blur-sm'>
                                <h3 className='mb-4 text-lg font-semibold text-center text-white'>
                                    Everything included in your plan:
                                </h3>
                                <div className='grid gap-3 sm:grid-cols-2'>
                                    {[
                                        'Personalized roadmaps',
                                        'AI chat assistant',
                                        'Curated resource library',
                                        'Community Support',
                                        'Progress tracking',
                                        'Regular updates',
                                        'Task management',
                                        'Cancel anytime',
                                    ].map((feature) => (
                                        <div key={feature} className='flex items-center space-x-3 text-white'>
                                            <div className='flex items-center justify-center flex-shrink-0 w-5 h-5 bg-green-400 rounded-full'>
                                                <Check className='w-3 h-3 text-white' />
                                            </div>
                                            <span className='text-sm'>{feature}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {checkoutError && (
                                <div className='mb-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3'>
                                    <p className='text-sm leading-relaxed text-amber-800'>{checkoutError}</p>
                                </div>
                            )}

                            <button
                                type='button'
                                onClick={redirectToPaymentGateway}
                                disabled={isLoading}
                                className={`w-full rounded-xl bg-white/90 px-8 py-4 text-lg font-bold transition-all duration-200 ${
                                    isLoading ? 'opacity-50' : 'text-cyan-700 shadow-lg hover:scale-105 hover:shadow-xl'
                                }`}
                            >
                                {isLoading ? (
                                    <div className='flex items-center justify-center space-x-2'>
                                        <Loader2 className='w-5 h-5 animate-spin' />
                                        <span>Processing...</span>
                                    </div>
                                ) : (
                                    trialPeriodDays > 0 ? `Start Your ${trialPeriodDays}-Day Free Trial` : 'Subscribe Now'
                                )}
                            </button>

                            <p className='mt-4 text-xs text-center text-white/80'>
                                {isTestMode
                                    ? 'Test mode — Use card 4242 4242 4242 4242, any future date, any CVC'
                                    : trialPeriodDays > 0
                                        ? 'No credit card required • Cancel anytime • Secure payment'
                                        : 'Cancel anytime • Secure payment powered by Stripe'
                                }
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

// Feedback survey shown on 2nd+ "Maybe Later" click
type FeedbackReason = 'still_exploring' | 'price_high' | 'not_right' | 'other'

const FEEDBACK_OPTIONS: { value: FeedbackReason; label: string }[] = [
    { value: 'still_exploring', label: "I'm still exploring" },
    { value: 'price_high', label: 'The price feels a bit high' },
    { value: 'not_right', label: "I don't think BrightPath is right for me" },
    { value: 'other', label: 'Other (Please specify)' },
]

function PaywallFeedbackSurvey({ onSubmit, onSkip }: {
    onSubmit: (reason: FeedbackReason, otherText?: string) => void
    onSkip: () => void
}) {
    const [selectedReason, setSelectedReason] = useState<FeedbackReason | null>(null)
    const [otherText, setOtherText] = useState('')

    return (
        <div className='p-6 rounded-2xl bg-white/15 backdrop-blur-sm'>
            <h3 className='mb-2 text-lg font-bold text-center text-white'>
                Quick question before you go
            </h3>
            <p className='mb-4 text-sm text-center text-white/80'>
                What's holding you back from subscribing? Your feedback helps us improve.
            </p>
            <div className='space-y-2'>
                {FEEDBACK_OPTIONS.map((option) => (
                    <button
                        key={option.value}
                        type='button'
                        onClick={() => setSelectedReason(option.value)}
                        className={`w-full rounded-xl p-3 text-left text-sm transition-all duration-200 border ${
                            selectedReason === option.value
                                ? 'border-white bg-white/25 text-white font-medium'
                                : 'border-white/20 bg-white/5 text-white/90 hover:bg-white/15'
                        }`}
                    >
                        <div className='flex items-center gap-3'>
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                selectedReason === option.value ? 'border-white' : 'border-white/40'
                            }`}>
                                {selectedReason === option.value && <div className='w-2 h-2 rounded-full bg-white' />}
                            </div>
                            {option.label}
                        </div>
                    </button>
                ))}
            </div>
            {selectedReason === 'other' && (
                <textarea
                    value={otherText}
                    onChange={(e) => setOtherText(e.target.value.slice(0, 200))}
                    placeholder='Tell us more (optional, max 200 characters)'
                    maxLength={200}
                    className='mt-3 w-full rounded-xl border border-white/20 bg-white/10 p-3 text-sm text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 resize-none'
                    rows={2}
                />
            )}
            <div className='mt-4 flex gap-3'>
                <button
                    type='button'
                    onClick={onSkip}
                    className='flex-1 rounded-xl border border-white/30 px-4 py-2.5 text-sm font-medium text-white/80 hover:bg-white/10 transition-colors'
                >
                    Skip
                </button>
                <button
                    type='button'
                    onClick={() => selectedReason && onSubmit(selectedReason, selectedReason === 'other' ? otherText : undefined)}
                    disabled={!selectedReason}
                    className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                        selectedReason
                            ? 'bg-white text-cyan-700 hover:bg-white/90'
                            : 'bg-white/20 text-white/50 cursor-not-allowed'
                    }`}
                >
                    Submit
                </button>
            </div>
        </div>
    )
}

// Usage-based paywall modal — matches Junko's Lovable prototype
function UsagePaywallModal({
    isOpen,
    onClose,
    isTestMode,
    plan,
    setPlan,
    isLoading,
    checkoutError,
    redirectToPaymentGateway,
}: {
    isOpen: boolean
    onClose: () => void
    isTestMode: boolean
    plan: 'monthly' | 'annual'
    setPlan: (plan: 'monthly' | 'annual') => void
    isLoading: boolean
    checkoutError: string | null
    redirectToPaymentGateway: () => void
}) {
    const { usageStatus } = usePaywall()
    const [showSurvey, setShowSurvey] = useState(false)

    if (!isOpen) return null

    const dismissCount = usageStatus?.paywallDismissCount ?? 0

    const handleMaybeLater = () => {
        // Always show survey on every dismiss
        setShowSurvey(true)
    }

    const handleSurveySubmit = (reason: FeedbackReason, otherText?: string) => {
        api(ROUTES.app.paywallDismiss, { reason, otherText }).catch(() => {})
        setShowSurvey(false)
        onClose()
    }

    const handleSurveySkip = () => {
        api(ROUTES.app.paywallDismiss, {}).catch(() => {})
        setShowSurvey(false)
        onClose()
    }

    return (
        <>
            <div className='fixed inset-0 z-40 min-h-screen bg-gradient-to-br from-cyan-600 via-cyan-500 to-cyan-600' />

            <div className='fixed inset-0 z-50 overflow-y-auto'>
                <div className='flex items-center justify-center min-h-full p-4 pb-24'>
                    <div className='w-full max-w-md'>
                        {isTestMode && (
                            <div className='mb-4 px-4 py-2 bg-orange-400 rounded-lg text-center'>
                                <span className='text-sm font-bold text-white uppercase tracking-wide'>
                                    Stripe Test Mode — No real charges
                                </span>
                            </div>
                        )}

                        {showSurvey ? (
                            <>
                                <div className='mb-6 text-center'>
                                    <h2 className='mb-2 text-2xl font-bold text-white drop-shadow-lg'>
                                        We'd love your feedback
                                    </h2>
                                </div>
                                <PaywallFeedbackSurvey
                                    onSubmit={handleSurveySubmit}
                                    onSkip={handleSurveySkip}
                                />
                            </>
                        ) : (
                            <>
                                {/* Header with checkmark icon */}
                                <div className='mb-8 text-center'>
                                    <div className='flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-white/20'>
                                        <svg className='w-8 h-8 text-white' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
                                        </svg>
                                    </div>
                                    <h2 className='mb-3 text-3xl font-bold text-white drop-shadow-lg'>
                                        You're off to a great start!
                                    </h2>
                                    <p className='text-base text-white/90 leading-relaxed'>
                                        You've already taken an important step in supporting your child.
                                        BrightPath can continue to support your care journey with clarity and confidence.
                                    </p>
                                </div>

                                {/* Plan selection — stacked cards */}
                                <p className='text-sm text-white/80 text-center mb-3'>Select a plan:</p>
                                <div className='space-y-4 mb-6'>
                                    <button
                                        type='button'
                                        onClick={() => setPlan('monthly')}
                                        className={`relative w-full rounded-2xl bg-white text-gray-900 shadow-lg p-5 text-left transition-all duration-200 border-2 ${
                                            plan === 'monthly'
                                                ? 'border-cyan-600 ring-2 ring-cyan-300'
                                                : 'border-transparent hover:border-gray-200'
                                        }`}
                                    >
                                        <div className='flex items-center justify-between'>
                                            <div className='flex items-center gap-3'>
                                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                                    plan === 'monthly' ? 'border-cyan-600' : 'border-gray-300'
                                                }`}>
                                                    {plan === 'monthly' && <div className='w-2.5 h-2.5 rounded-full bg-cyan-600' />}
                                                </div>
                                                <h3 className='text-lg font-semibold'>Monthly</h3>
                                            </div>
                                            <div className='flex items-baseline gap-1'>
                                                <span className='text-2xl font-bold'>$4.99</span>
                                                <span className='text-sm text-gray-500'>/ month</span>
                                            </div>
                                        </div>
                                    </button>

                                    <button
                                        type='button'
                                        onClick={() => setPlan('annual')}
                                        className={`relative w-full rounded-2xl bg-white text-gray-900 shadow-lg p-5 text-left transition-all duration-200 border-2 ${
                                            plan === 'annual'
                                                ? 'border-cyan-600 ring-2 ring-cyan-300'
                                                : 'border-transparent hover:border-gray-200'
                                        }`}
                                    >
                                        <div className='absolute px-3 py-1 text-xs font-bold bg-yellow-400 rounded-full right-4 -top-3 text-cyan-800'>
                                            BEST VALUE
                                        </div>
                                        <div className='flex items-center justify-between'>
                                            <div className='flex items-center gap-3'>
                                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                                    plan === 'annual' ? 'border-cyan-600' : 'border-gray-300'
                                                }`}>
                                                    {plan === 'annual' && <div className='w-2.5 h-2.5 rounded-full bg-cyan-600' />}
                                                </div>
                                                <div>
                                                    <h3 className='text-lg font-semibold'>Annual</h3>
                                                    <span className='text-sm line-through text-gray-400'>$59</span>
                                                </div>
                                            </div>
                                            <div className='text-right'>
                                                <div className='flex items-baseline gap-1'>
                                                    <span className='text-2xl font-bold'>$49</span>
                                                    <span className='text-sm text-gray-500'>/ year</span>
                                                </div>
                                                <p className='text-xs text-green-500 font-medium'>Save $10.88 per year</p>
                                            </div>
                                        </div>
                                    </button>
                                </div>

                                {/* Features card */}
                                <div className='p-6 mb-6 rounded-2xl bg-white/15 backdrop-blur-sm'>
                                    <h3 className='mb-1 text-lg font-bold text-center text-white'>
                                        BrightPath Starter Plan
                                    </h3>
                                    <p className='mb-4 text-sm text-center text-white/80'>
                                        Everything included in your plan:
                                    </p>
                                    <div className='space-y-3'>
                                        {[
                                            'Personalized roadmap that adapts to your child\u2019s development',
                                            'Milestone-based checklist & progress tracking',
                                            'Daily tips and guidance',
                                            'Curated resource library',
                                            'Autism-trained chat assistant',
                                            'Cancel anytime',
                                        ].map((feature) => (
                                            <div key={feature} className='flex items-start gap-3 text-white'>
                                                <div className='flex items-center justify-center flex-shrink-0 w-5 h-5 mt-0.5 bg-green-400 rounded-full'>
                                                    <Check className='w-3 h-3 text-white' />
                                                </div>
                                                <span className='text-sm leading-snug'>{feature}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {checkoutError && (
                                    <div className='mb-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3'>
                                        <p className='text-sm leading-relaxed text-amber-800'>{checkoutError}</p>
                                    </div>
                                )}

                                {/* CTA button */}
                                <button
                                    type='button'
                                    onClick={redirectToPaymentGateway}
                                    disabled={isLoading}
                                    className={`w-full rounded-xl bg-white px-8 py-4 text-lg font-bold transition-all duration-200 ${
                                        isLoading ? 'opacity-50' : 'text-cyan-700 shadow-lg hover:scale-105 hover:shadow-xl'
                                    }`}
                                >
                                    {isLoading ? (
                                        <div className='flex items-center justify-center space-x-2'>
                                            <Loader2 className='w-5 h-5 animate-spin' />
                                            <span>Processing...</span>
                                        </div>
                                    ) : (
                                        'Continue My BrightPath'
                                    )}
                                </button>

                                {/* Maybe Later link */}
                                <div className='mt-4 text-center'>
                                    <button
                                        type='button'
                                        onClick={handleMaybeLater}
                                        className='text-sm text-white/80 underline underline-offset-2 hover:text-white transition-colors'
                                    >
                                        Maybe Later
                                    </button>
                                </div>

                                <p className='mt-4 text-xs text-center text-white/60'>
                                    {isTestMode
                                        ? 'Test mode — Use card 4242 4242 4242 4242, any future date, any CVC'
                                        : 'Cancel anytime • Secure payment powered by Stripe'
                                    }
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}
